import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { PAGES } from '../../constants/pages';
import { useApplicantData } from '../../hooks/useApplicantData';
import { getApplicantData } from '../../util/helpers/applicantDataHelper';
import KycLink from './KycLink';
import BaseLayout from '../../components/BaseLayout';
import Header from '../../components/Header';
import KycSelectionContainer from '../../components/KycSelectionContainer';
import { selectRequiredDocuments } from '../../store/selectors/documentsSelectors';
import useKycProofOfIdentityDocuments from '../../hooks/useKycProofOfIdentityDocuments';
import {
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE_GTM,
  KYC_SELECTED_OPTION,
} from '../../constants/sessionStorage';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import useStepPost from '../../hooks/useStepPost';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';

const Wrapper = styled.div`
  padding: 50px 0;
  display: flex;
  flex-direction: column;

  @media only screen and (max-width: 500px) {
    padding: 0;
  }
`;

const KycProofOfIdentity = ({ applicantType }) => {
  const { t } = useTranslation();
  const ApplicantData = useApplicantData(applicantType);
  const { onSuccess: onSuccessSkip } = useStepPost({
    nextPath: PAGES.KYC_DONE_SKIP,
  });
  const requiredDocuments = useSelector(selectRequiredDocuments);
  const { kycIdentityOptions } = useKycProofOfIdentityDocuments(
    requiredDocuments,
    applicantType,
  );

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Kyc Proof of identity',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Kyc Proof of identity',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const onClick = (option) => {
    handleGtmEvent();
    storeInSessionStorage(KYC_SELECTED_OPTION, option);
  };

  const options = kycIdentityOptions();
  const { BrokerName } = useSelector(selectSettings);

  return (
    <BaseLayout
      previousPath={PAGES.KYC_VERIFICATION}
      isKycProcess="true"
      noLogoBox
    >
      <Helmet>
        <title>
          {t('kycVerification.titleTagProofOfIdentity', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <Wrapper>
        <Header
          title={t('kycVerification.proofOfIdentityNamed', {
            name: getApplicantData(ApplicantData, 'PersonName', 'FirstName'),
          })}
          subtitle={t('kycVerification.proofOfIdentitySubtitle')}
          subTitleMb={24}
        />

        {options?.map((option) => (
          <KycSelectionContainer
            key={option.path}
            {...option}
            onClick={onClick}
          />
        ))}
        <KycLink
          onClick={() => {
            handleGtmEvent();
            onSuccessSkip();
          }}
        />
      </Wrapper>
    </BaseLayout>
  );
};

KycProofOfIdentity.propTypes = {
  applicantType: PropTypes.string,
};

export default KycProofOfIdentity;
